import { DESCRIPTION_MAX, type Environment, type EnvironmentRef, NAME_MAX, type SectionSummary } from '@fleequid-secrets/shared';
import { type CSSProperties, type ReactNode, useState } from 'react';
import { api, messageOf } from '@/api/client';
import { plural } from '@/lib/format';
import { useOverlay } from '@/lib/overlay';
import { Cancelled, guarded, useShell } from '@/lib/shell';
import { Icon } from './Icon';

function Dialog(props: { title: string; lead: ReactNode; onClose: () => void; children: ReactNode }): ReactNode {
    useOverlay();

    return (
        <div className="scrim" onMouseDown={(event) => event.target === event.currentTarget && props.onClose()}>
            <div className="dialog float" onKeyDown={(event) => event.key === 'Escape' && props.onClose()}>
                <div className="sheet-head">
                    <div>
                        <h2>{props.title}</h2>
                        <p>{props.lead}</p>
                    </div>
                    <button className="closebtn" onClick={props.onClose} title="Close (esc)">
                        <Icon name="x" size={17} />
                    </button>
                </div>
                <div className="dialog-body">{props.children}</div>
            </div>
        </div>
    );
}

const Place = (props: { environment: EnvironmentRef; section?: string }): ReactNode => (
    <span className="envtag">
        <i className="dot" style={{ '--c': props.environment.color } as CSSProperties} />
        {props.environment.name}
        {props.section ? ` · ${props.section}` : ''}
    </span>
);

// A section's name and description. Its address follows its name, and with it the comparison across environments.
export function RenameSection(props: {
    environment: EnvironmentRef;
    section: SectionSummary;
    onClose: () => void;
    onDone: (section: SectionSummary) => void;
}): ReactNode {
    const shell = useShell();
    const [name, setName] = useState(props.section.name);
    const [description, setDescription] = useState(props.section.description);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    return (
        <Dialog title="Rename the section" lead={<Place environment={props.environment} section={props.section.name} />} onClose={props.onClose}>
            <form
                className="dialog-body"
                style={{ padding: 0 }}
                onSubmit={(event) => {
                    event.preventDefault();
                    setBusy(true);
                    guarded(shell, props.environment, `rename ${props.section.name}`, (confirm) =>
                        api.updateSection(props.section.id, { name: name.trim(), description: description.trim() }, confirm),
                    )
                        .then(({ section }) => props.onDone(section))
                        .catch((reason: unknown) => !(reason instanceof Cancelled) && setError(messageOf(reason)))
                        .finally(() => setBusy(false));
                }}
            >
                <label>
                    <span className="micro">Name · the same name in every environment keeps them comparable</span>
                    <input value={name} maxLength={NAME_MAX} onChange={(event) => setName(event.target.value)} autoFocus />
                </label>
                <label>
                    <span className="micro">Description · optional</span>
                    <input
                        value={description}
                        maxLength={DESCRIPTION_MAX}
                        onChange={(event) => setDescription(event.target.value)}
                        placeholder="What runs with it"
                    />
                </label>
                {error && <div className="form-error">{error}</div>}
                <div className="dialog-actions">
                    <button type="button" className="act" onClick={props.onClose}>
                        Cancel
                    </button>
                    <button className="act primary" disabled={busy || !name.trim()}>
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </form>
        </Dialog>
    );
}

// The section's keys into another environment, with their values or empty to fill there: what is already there stays.
export function CopySection(props: {
    environment: EnvironmentRef;
    section: SectionSummary;
    environments: readonly Environment[];
    onClose: () => void;
    onDone: (target: Environment, sectionSlug: string) => void;
}): ReactNode {
    const shell = useShell();
    const others = props.environments.filter((environment) => environment.id !== props.environment.id);
    const [targetId, setTargetId] = useState(others[0]?.id ?? '');
    const [values, setValues] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const target = others.find((environment) => environment.id === targetId);

    return (
        <Dialog
            title="Copy to another environment"
            lead={
                <>
                    {plural(props.section.variableCount, 'key')} of <Place environment={props.environment} section={props.section.name} />
                </>
            }
            onClose={props.onClose}
        >
            <label>
                <span className="micro">Into</span>
                <select value={targetId} onChange={(event) => setTargetId(event.target.value)}>
                    {others.map((environment) => (
                        <option key={environment.id} value={environment.id}>
                            {environment.name}
                            {environment.sections.some((section) => section.slug === props.section.slug)
                                ? ''
                                : ` (a new ${props.section.name} there)`}
                        </option>
                    ))}
                </select>
            </label>
            <button className={`toggle ${values ? 'on' : ''}`} onClick={() => setValues(!values)}>
                <span className="sw" />
                With their values. Off, the keys arrive empty, to fill with that environment's own.
            </button>
            <p>Keys already in {target?.name ?? 'it'} stay as they are.</p>
            {error && <div className="form-error">{error}</div>}
            <div className="dialog-actions">
                <button className="act" onClick={props.onClose}>
                    Cancel
                </button>
                <button
                    className="act primary"
                    disabled={busy || !target}
                    onClick={() => {
                        if (!target) {
                            return;
                        }
                        setBusy(true);
                        guarded(shell, target, `copy the keys of ${props.section.name} into ${target.name}`, (confirm) =>
                            api.copySection(props.section.id, target.id, values, confirm),
                        )
                            .then((outcome) => {
                                shell.say(
                                    `${plural(outcome.added, 'key')} copied into ${target.name}${outcome.skipped ? `, ${outcome.skipped} already there` : ''}`,
                                    {
                                        label: 'Open',
                                        run: () => props.onDone(target, outcome.section.slug),
                                    },
                                );
                                props.onClose();
                            })
                            .catch((reason: unknown) => !(reason instanceof Cancelled) && setError(messageOf(reason)))
                            .finally(() => setBusy(false));
                    }}
                >
                    <Icon name="swap" size={15} />
                    {busy ? 'Copying…' : 'Copy'}
                </button>
            </div>
        </Dialog>
    );
}

// A section, its variables and their history, gone: its address typed to confirm.
export function DeleteSection(props: { environment: EnvironmentRef; section: SectionSummary; onClose: () => void; onDone: () => void }): ReactNode {
    const shell = useShell();
    const [typed, setTyped] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    return (
        <Dialog
            title={`Delete ${props.section.name}?`}
            lead={<Place environment={props.environment} section={props.section.name} />}
            onClose={props.onClose}
        >
            <form
                className="dialog-body"
                style={{ padding: 0 }}
                onSubmit={(event) => {
                    event.preventDefault();
                    setBusy(true);
                    guarded(shell, props.environment, `delete ${props.section.name}`, (confirm) =>
                        api.deleteSection(props.section.id, typed, confirm),
                    )
                        .then(props.onDone)
                        .catch((reason: unknown) => !(reason instanceof Cancelled) && setError(messageOf(reason)))
                        .finally(() => setBusy(false));
                }}
            >
                <p>
                    Its <b>{plural(props.section.variableCount, 'variable')}</b> and every version of them go with it, for good. Type{' '}
                    <b>{props.section.slug}</b> to confirm.
                </p>
                <input
                    value={typed}
                    onChange={(event) => setTyped(event.target.value)}
                    autoFocus
                    spellCheck={false}
                    placeholder={props.section.slug}
                />
                {error && <div className="form-error">{error}</div>}
                <div className="dialog-actions">
                    <button type="button" className="act" onClick={props.onClose}>
                        Cancel
                    </button>
                    <button className="act danger solid" disabled={busy || typed !== props.section.slug}>
                        {busy ? 'Deleting…' : 'Delete the section'}
                    </button>
                </div>
            </form>
        </Dialog>
    );
}
